/** ECRANUL «LISTA DE CANTITĂȚI»: toate specialitățile proiectului, pe
 *  subiecte și capitole, fiecare articol cu cantitatea (și de unde vine),
 *  prețul unitar desfăcut pe material / manoperă / utilaj / transport și
 *  resursele consumate.
 *
 *  Subiectele se pot alege (cipurile de sus): tabelul și exportul arată numai
 *  ce e ales. Un rând desfăcut arată rețeta aplicată (resursele, cu prețul
 *  fiecăreia și sursa lui), cantitatea pe corpuri și explicația măsurătorii.
 *  Rândurile ESTIMATE (cantitate dedusă din apartamente și arii, nu din
 *  planșe) sunt marcate: lista spune ce e măsurat și ce e presupus.
 *
 *  Catalogul (rețetele și resursele, globale și copiile proiectului) e al
 *  doilea ecran al aceleiași file (`CatalogBoq`).
 *
 *  PIAȚA. Articolele devizelor (betonul pe clase, armătura) n-au resurse în
 *  listă, deci prețul lor de piață se explorează de pe rândul lor: butonul
 *  «Piața» apare pe un articol al cărui preț îl urmărește colectorul
 *  (`ExploreazaPiata`). Un preț pus din piață se deosebește de unul dintr-o
 *  ofertă după eticheta lui. */
import { useCallback, useEffect, useMemo, useState } from "react";
import Termeni, { Estimat, Note } from "./Termeni";
import SeIncarca from "../../components/SeIncarca";
import { esteCheie, numar, t, useT } from "../../i18n";
import { bani, pretUnitar } from "../../structure/bani";
import {
  caleExport, cauta, subiecteVazute, totalVazut,
  type Articol, type Bani, type FelCantitate, type ListaCantitati as Lista, type Subiect,
} from "./boq";
import ExploreazaPiata from "./ExploreazaPiata";
import { cheiaArticolului, dinPiata, getPiataResurse, type PiataProiect, type Tinta } from "./piataResurse";
import "./piataResurse.css";

const nr = (v: number | null | undefined, z = 2) =>
  v == null ? "—" : numar(v, { maximumFractionDigits: z });
const UM: Record<string, string> = { m2: "m²", m3: "m³" };
const um = (u: string) => UM[u] ?? u;
const numeSubiect = (cod: string, implicit: string) => {
  const k = `lc.subiect.${cod}`;
  return esteCheie(k) ? t(k) : implicit;
};
const numeCapitol = (subiect: string, cod: string, implicit: string) => {
  const k = `lc.capitol.${subiect}.${cod}`;
  return esteCheie(k) ? t(k) : implicit;
};
const numeFel = (fel: FelCantitate) => t(`lc.fel.${fel}`);
const numeIndirect = (cheie: string) => {
  const k = `struct.ret.ind.${cheie}`;
  return esteCheie(k) ? t(k) : cheie;
};

/** Prețul de material afișat include utilajele și transportul — serviciile
 *  cumpărate, nu munca — ca în devizul structurii și în fișierul exportat. */
const puMaterial = (b: Bani) => b.material + b.utilaj + b.transport;

function Marcaj({ fel }: { fel: FelCantitate }) {
  return <span className={`lc-fel lc-fel-${fel}`}>{numeFel(fel)}</span>;
}

function Detaliu({ a }: { a: Articol }) {
  useT();
  const corpuri = Object.entries(a.pe_corp ?? {});
  return (
    <div className="lc-det">
      <dl>
        <dt>{t("lc.det.cantitatea")}</dt>
        <dd>
          <Marcaj fel={a.sursa_cantitate.fel} />
          {a.sursa_cantitate.explicatie && <span> · {a.sursa_cantitate.explicatie}</span>}
          {a.sursa_cantitate.calculata != null && (
            <span className="sg-muted"> · {t("lc.det.calculata", { v: nr(a.sursa_cantitate.calculata) })}</span>
          )}
        </dd>
        {corpuri.length > 0 && (
          <>
            <dt>{t("lc.det.pe_corpuri")}</dt>
            <dd className="lc-corpuri">
              {corpuri.map(([c, v]) => <span key={c} className="rc-chip">{c} · {nr(v)}</span>)}
            </dd>
          </>
        )}
        {a.specificatii && (<><dt>{t("lc.det.specificatii")}</dt><dd>{a.specificatii}</dd></>)}
        {a.observatii && (<><dt>{t("lc.det.observatii")}</dt><dd>{a.observatii}</dd></>)}
        <dt>{t("lc.det.pret")}</dt>
        <dd>
          {a.sursa_pret}
          {a.reteta && <span className="sg-muted"> · {t("lc.det.reteta", { cod: a.reteta })}</span>}
          {a.din_proiect && <span className="lc-fel lc-fel-om" style={{ marginLeft: 6 }}>{t("lc.catalog.din_proiect")}</span>}
          {a.pret_impus && (
            <div className="sg-muted">{t("lc.sc.pret_inainte", { p: pretUnitar(a.pret_impus.inainte, um(a.um)) })}</div>
          )}
        </dd>
      </dl>
      {a.resurse.length > 0 && (
        <table className="sg-table lc-resurse">
          <thead>
            <tr>
              <th>{t("lc.res.tip")}</th><th>{t("lc.res.resursa")}</th><th>{t("lc.res.consum")}</th>
              <th>{t("lc.res.cantitate")}</th><th>{t("lc.res.pret")}</th><th>{t("lc.res.total")}</th>
            </tr>
          </thead>
          <tbody>
            {a.resurse.map((r, i) => (
              <tr key={i} className={r.lipsa ? "lc-lipsa" : undefined}>
                <td className="sg-muted">{t(`lc.tip.${r.tip}`)}</td>
                <td>{r.nume}{r.din_proiect && <span className="lc-fel lc-fel-om" style={{ marginLeft: 6 }}>{t("lc.catalog.din_proiect")}</span>}</td>
                <td className="sg-mono">{nr(r.consum, 3)} {um(r.um)}/{um(a.um)}</td>
                <td className="sg-mono">{nr(r.cantitate)} {um(r.um)}</td>
                <td className="sg-mono">{pretUnitar(r.pret_unitar, um(r.um))}</td>
                <td className="sg-mono">{bani(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {a.din_deviz && <div className="sg-muted" style={{ marginTop: 6 }}>{t(`lc.din_deviz.${a.din_deviz}`)}</div>}
    </div>
  );
}

/** Ce trebuie unui rând ca să deschidă piața: țintele și deschiderea. */
type Piata = { tinte: Tinta[] | null; laPiata: ((cheie: string) => void) | null };

function TabelSubiect({ s, text, piata }: { s: Subiect; text: string; piata: Piata }) {
  useT();
  const [deschis, setDeschis] = useState<string | null>(null);
  const capitole = useMemo(() => cauta(s, text), [s, text]);
  if (capitole.length === 0) return null;
  return (
    <div className="lc-subiect">
      <div className="lc-subiect-h">
        <span className="lc-nr">{s.nr}</span>
        <h3>{numeSubiect(s.cod, s.nume)}</h3>
        <span className="sg-muted">
          {t("lc.subiect.rezumat", { n: s.articole, est: s.estimate })}
        </span>
        <span className="lc-total">{bani(s.total.total)}</span>
      </div>
      <div className="bc-scroll">
        <table className="sg-table rc-deviz lc-tabel">
          <thead>
            <tr>
              <th>{t("struct.ret.th_nr")}</th>
              <th>{t("struct.ret.th_articol")}</th>
              <th><Termeni text={t("struct.ret.th_um")} /></th>
              <th className="bc-n">{t("struct.ret.th_cantitate")}</th>
              <th className="bc-n"><Termeni text={t("struct.ret.th_pu_material")} /></th>
              <th className="bc-n"><Termeni text={t("struct.ret.p_u_manopera")} /></th>
              <th className="bc-n"><Termeni text={t("lc.th_pu_total")} /></th>
              <th className="bc-n">{t("lc.th_total")}</th>
            </tr>
          </thead>
          <tbody>
            {capitole.map((c) => (
              <CapitolRanduri key={c.cod} subiect={s.cod} c={c} deschis={deschis} setDeschis={setDeschis} piata={piata} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7}><strong>{t("lc.total_subiect", { s: numeSubiect(s.cod, s.nume) })}</strong></td>
              <td className="sg-mono bc-n"><strong>{bani(s.total.total)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function CapitolRanduri({ subiect, c, deschis, setDeschis, piata }: {
  subiect: string; c: Subiect["capitole"][number]; deschis: string | null; setDeschis: (k: string | null) => void;
  piata: Piata;
}) {
  return (
    <>
      <tr className="rc-capitol lc-capitol">
        <td className="sg-mono">{c.nr}</td>
        <td colSpan={6}><strong>{numeCapitol(subiect, c.cod, c.nume).toUpperCase()}</strong></td>
        <td className="sg-mono bc-n"><strong>{bani(c.total.total)}</strong></td>
      </tr>
      {c.articole.map((a) => {
        const e = deschis === a.nr;
        const cheiePiata = a.din_deviz && piata.laPiata ? cheiaArticolului(piata.tinte, a.cheie_pret) : null;
        return (
          <span key={a.nr} style={{ display: "contents" }}>
            <tr className={`lc-rand${a.componenta ? " lc-comp" : ""}`} onClick={() => setDeschis(e ? null : a.nr)}
                style={{ cursor: "pointer" }} title={a.sursa_pret}>
              <td className="sg-mono">{a.nr}</td>
              <td>
                <span className="bc-sag">{e ? "▾" : "▸"}</span>
                {a.denumire}
                {a.sursa_cantitate.fel === "om" && <Marcaj fel="om" />}
                {a.din_proiect && <span className="lc-fel lc-fel-om">{t("lc.catalog.din_proiect")}</span>}
                {a.pret_impus && (dinPiata(a.pret_impus.sursa)
                  ? <span className="lc-fel lc-fel-piata" title={a.pret_impus.sursa}>{t("piata_res.chip")}</span>
                  : <span className="lc-fel lc-fel-sc" title={a.pret_impus.sursa}>{t("lc.sc.din_oferta")}</span>)}
                {a.resurse_lipsa.length > 0 && <span className="lc-fel lc-fel-lipsa">{t("lc.fara_pret")}</span>}
                {cheiePiata && (
                  <button type="button" className="btn lc-x lc-piata-btn" title={t("piata_res.buton_t")}
                          onClick={(ev) => { ev.stopPropagation(); piata.laPiata?.(cheiePiata); }}>{t("piata_res.buton")}</button>
                )}
              </td>
              <td className="sg-mono">{um(a.um)}</td>
              <td className="sg-mono bc-n">
                {a.sursa_cantitate.fel === "estimat" && <Estimat deCe={a.sursa_cantitate.explicatie} />}
                {nr(a.cantitate)}</td>
              <td className="sg-mono bc-n">{puMaterial(a.pu) ? pretUnitar(puMaterial(a.pu)) : "—"}</td>
              <td className="sg-mono bc-n">{a.pu.manopera ? pretUnitar(a.pu.manopera) : "—"}</td>
              <td className="sg-mono bc-n">{pretUnitar(a.pu.total)}</td>
              <td className="sg-mono bc-n">{bani(a.total.total)}</td>
            </tr>
            {e && (
              <tr>
                <td colSpan={8} className="rc-detaliu"><Detaliu a={a} /></td>
              </tr>
            )}
          </span>
        );
      })}
    </>
  );
}

export default function ListaCantitati({ projectId, solutionId, scenariu = null, lista, eroare, seIncarca, reincarca,
                                         laCatalog, potScrie = false }: {
  projectId: string;
  /** dreptul de a scrie: importul prețurilor de piață îl cere */
  potScrie?: boolean;
  /** scenariul curent, când are rețetele, prețurile sau programul lui: lista e a lui */
  scenariu?: { id: string; nume: string } | null;
  /** soluția pe care se face lista: detaliul structurii sau, până la el,
   *  arhitectura din fir; fără niciuna, lista așteaptă */
  solutionId: string | null;
  /** lista, citită de părinte (`Retete.tsx`) — rezumatul stă pe ea */
  lista: Lista | null;
  eroare: string | null;
  seIncarca: boolean;
  reincarca: (refa?: boolean) => Promise<void>;
  /** fila «Rețete»: catalogul tuturor rețetelor, cu copiile proiectului */
  laCatalog: () => void;
}) {
  useT();
  const [alese, setAlese] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const incarca = reincarca;
  // țintele pieței pe lista asta: citite după listă, ca butoanele să fie ale
  // ei; o citire căzută înseamnă doar că butoanele lipsesc
  const [piataProiect, setPiataProiect] = useState<PiataProiect | null>(null);
  const [revPiata, setRevPiata] = useState(0);
  const [explorata, setExplorata] = useState<string | null>(null);
  const amprenta = lista?.amprenta ?? null;
  useEffect(() => {
    if (!solutionId || !amprenta) return;
    let viu = true;
    getPiataResurse(projectId, solutionId).then((d) => { if (viu) setPiataProiect(d); },
                                                () => { if (viu) setPiataProiect(null); });
    return () => { viu = false; };
  }, [projectId, solutionId, amprenta, revPiata]);
  const dupaPiata = useCallback(() => {
    void incarca(true);
    setRevPiata((n) => n + 1);
  }, [incarca]);
  const piata: Piata = { tinte: piataProiect?.tinte ?? null, laPiata: piataProiect ? setExplorata : null };

  if (!solutionId) {
    return <div className="sg-muted" style={{ padding: "12px 0" }}>{t("lc.fara_detaliu")}</div>;
  }
  if (seIncarca && !lista) return <SeIncarca />;
  if (eroare && !lista) {
    return (
      <div className="sg-error">
        {t("lc.eroare", { e: eroare })}
        <button type="button" className="btn" style={{ marginLeft: 10 }} onClick={() => void incarca(true)}>{t("lc.reincearca")}</button>
      </div>
    );
  }
  if (!lista) return null;

  const vazute = subiecteVazute(lista, alese);
  const tot = totalVazut(vazute);
  const comuta = (cod: string) => setAlese((s) => {
    const n = new Set(s);
    if (n.has(cod)) n.delete(cod); else n.add(cod);
    return n;
  });
  const export_ = caleExport(projectId, solutionId, [...alese], scenariu?.id ?? null);
  const ctx = lista.context;

  return (
    <div className="bc lc">
      <div className="bc-bara">
        <span className="bc-lbl">{t("lc.titlu")}</span>
        <span className="sg-muted">{t("lc.generata", { la: lista.generat_la.slice(0, 16).replace("T", " "), cat: lista.versiune_catalog })}</span>
        {scenariu && <span className="lc-fel lc-fel-sc">{t("lc.sc.lista_scenariului", { nume: scenariu.nume })}</span>}
        <Note note={lista.note} />
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={laCatalog}>{t("lc.catalog.deschide")}</button>
        <button type="button" className="btn" disabled={seIncarca} onClick={() => void incarca(true)}>
          {seIncarca ? t("comun.se_incarca") : t("lc.recalculeaza")}</button>
        <a className="btn btnp" href={export_} download>
          {alese.size ? t("lc.export_subiecte", { n: alese.size }) : t("lc.export_tot")}</a>
      </div>

      <div className="bc-plci">
        <div className="bc-plc">
          <div className="bc-n2">{t("lc.plc.direct")}</div>
          <div className="bc-v">{bani(lista.total_direct.total)}</div>
          <div className="bc-d">{t("lc.plc.direct_d", { m: bani(lista.total_direct.material + lista.total_direct.utilaj + lista.total_direct.transport), l: bani(lista.total_direct.manopera) })}</div>
        </div>
        <div className="bc-plc">
          <div className="bc-n2">{t("lc.plc.general")}</div>
          <div className="bc-v">{bani(lista.total_general)}</div>
          <div className="bc-d">{t("lc.plc.general_d", { p: lista.indirecte.map((i) => `${numar(i.procent, { maximumFractionDigits: 1 })} %`).join(" + ") })}</div>
        </div>
        <div className="bc-plc">
          <div className="bc-n2">{t("lc.plc.pe_m2")}</div>
          <div className="bc-v">{lista.eur_pe_m2 != null ? `${bani(lista.eur_pe_m2)}/m²` : "—"}</div>
          <div className="bc-d">{t("lc.plc.pe_m2_d", { a: nr(lista.arie_desfasurata_m2, 0) })}</div>
        </div>
        <div className="bc-plc">
          <div className="bc-n2">{t("lc.plc.articole")}</div>
          <div className="bc-v">{lista.statistica.articole}</div>
          <div className="bc-d">{t("lc.plc.articole_d", { est: lista.statistica.estimate, dev: lista.statistica.din_devize, om: lista.statistica.puse_de_om })}</div>
        </div>
        <div className="bc-plc">
          <div className="bc-n2">{t("lc.plc.proiect")}</div>
          <div className="bc-v">{ctx.apartamente}</div>
          <div className="bc-d">{t("lc.plc.proiect_d", { c: ctx.corpuri.length, s: ctx.niveluri_subsol, p: nr(ctx.arie_parcaj_m2, 0) })}</div>
        </div>
      </div>

      <div className="lc-filtre">
        <div className="lc-chips" role="group" aria-label={t("lc.subiecte")}>
          {lista.subiecte.map((s) => (
            <button key={s.cod} type="button" className={`lc-chip${alese.has(s.cod) ? " lc-chip-ales" : ""}`}
                    aria-pressed={alese.has(s.cod)} onClick={() => comuta(s.cod)}>
              <span className="lc-nr">{s.nr}</span> {numeSubiect(s.cod, s.nume)}
              <span className="lc-chip-suma">{bani(s.total.total)}</span>
            </button>
          ))}
          {alese.size > 0 && (
            <button type="button" className="lc-chip lc-chip-sterge" onClick={() => setAlese(new Set())}>{t("lc.toate")}</button>
          )}
        </div>
        <input className="lc-cauta" type="search" value={text} placeholder={t("lc.cauta")}
               onChange={(e) => setText(e.target.value)} />
      </div>
      {alese.size > 0 && (
        <div className="sg-muted">{t("lc.vazut", { n: tot.articole, v: bani(tot.total.total) })}</div>
      )}

      {vazute.map((s) => <TabelSubiect key={s.cod} s={s} text={text} piata={piata} />)}
      {explorata && (
        <ExploreazaPiata projectId={projectId} solutieId={solutionId} cheie={explorata} potScrie={potScrie}
                         colectatLa={piataProiect?.colectat_la} laInchide={() => setExplorata(null)} laSchimbat={dupaPiata} />
      )}

      {alese.size === 0 && !text && (
        <div className="bc-rama" style={{ padding: 14 }}>
          <table className="sg-table">
            <tbody>
              <tr><td>{t("lc.rec.direct")}</td><td className="sg-mono bc-n">{bani(lista.total_direct.total)}</td></tr>
              {lista.indirecte.map((i) => (
                <tr key={i.cheie}>
                  <td className="sg-muted">{numeIndirect(i.cheie)} ({numar(i.procent, { maximumFractionDigits: 1 })} %)</td>
                  <td className="sg-mono bc-n">{bani(i.valoare)}</td>
                </tr>
              ))}
              <tr className="bc-total"><td><strong>{t("lc.rec.general")}</strong></td><td className="sg-mono bc-n"><strong>{bani(lista.total_general)}</strong></td></tr>
            </tbody>
          </table>
          {lista.neaplicate.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary className="sg-muted">{t("lc.neaplicate", { n: lista.neaplicate.length })}</summary>
              <ul className="lc-neaplicate">
                {lista.neaplicate.map((n) => <li key={n.cod}><b>{n.cod}</b> {n.denumire} <span className="sg-muted">— {n.motiv}</span></li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
